import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download, LogOut, Mail, Inbox, CheckCircle2, Clock, Eye, Archive, Presentation as PresentIcon, ExternalLink, RefreshCw, Globe } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const STATUS_OPTIONS = ["new", "read", "responded", "archived"];

const fmtIST = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  } catch {
    return iso;
  }
};

function StatusPill({ s }) {
  const map = {
    new: { bg: "rgba(212,175,55,0.15)", color: "#9c7c38", border: "rgba(156,124,56,0.4)" },
    read: { bg: "rgba(74,74,74,0.12)", color: "#4a4a4a", border: "rgba(74,74,74,0.3)" },
    responded: { bg: "rgba(46,71,59,0.12)", color: "#2e473b", border: "rgba(46,71,59,0.35)" },
    archived: { bg: "rgba(26,26,26,0.06)", color: "#888", border: "rgba(26,26,26,0.2)" },
  };
  const m = map[s] || map.new;
  return (
    <span
      className="inline-block px-2.5 py-1 text-[10px]"
      style={{ background: m.bg, color: m.color, border: `1px solid ${m.border}`, fontFamily: "Outfit", letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600 }}
    >
      {s}
    </span>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, e, a] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/enquiries"),
        api.get("/admin/analytics"),
      ]);
      setStats(s.data);
      setEnquiries(e.data);
      setAnalytics(a.data);
    } catch {
      // handled by interceptor
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      setEnquiries((prev) => prev.map((q) => (q.id === id ? { ...q, status } : q)));
      if (selected?.id === id) setSelected({ ...selected, status });
      load();
    } catch {
      // ignore
    }
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get("/admin/pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `BEAUTWELLZ_Investor_Brief_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      api.post("/analytics/track", { type: "pdf_download", path: "/admin/pdf" }).catch(() => {});
    } catch {
      alert("PDF generation failed. Please try again.");
    } finally {
      setPdfBusy(false);
    }
  };

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--ivory)" }} data-testid="admin-page">
      {/* Top Bar */}
      <header className="crystal-header sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 border border-[var(--bronze)] flex items-center justify-center">
              <span className="font-serif-display text-[var(--bronze)]" style={{ fontSize: 14 }}>B</span>
            </div>
            <div className="leading-tight">
              <div className="font-serif-display text-[15px] tracking-tight">BEAUTWELLZ</div>
              <div className="overline" style={{ fontSize: 9 }}>Admin Dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="btn-ghost" data-testid="view-site-link"><ExternalLink size={14} /> View Site</Link>
            <Link to="/presentation" className="btn-ghost" data-testid="view-presentation-link"><PresentIcon size={14} /> Presentation</Link>
            <button onClick={onLogout} className="btn-ghost" data-testid="logout-button">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="overline">Welcome back</div>
            <h1 className="font-serif-display text-[40px] leading-tight mt-1" data-testid="admin-greeting">
              {user?.name || "Admin"}.
            </h1>
            <div className="text-[13px] mt-1" style={{ color: "var(--ink-2)" }}>{user?.email}</div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button onClick={load} className="btn-ghost" data-testid="refresh-button">
              <RefreshCw size={14} /> Refresh
            </button>
            <button onClick={downloadPdf} disabled={pdfBusy} className="btn-primary" data-testid="download-pdf-button">
              <Download size={14} /> {pdfBusy ? "Generating PDF..." : "Download Investor PDF"}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10" data-testid="stats-grid">
          {[
            { k: "Total Enquiries", v: stats?.total ?? "—", i: <Inbox size={14} /> },
            { k: "This Month", v: stats?.this_month ?? "—", i: <Clock size={14} /> },
            { k: "This Week", v: stats?.this_week ?? "—", i: <Clock size={14} /> },
            { k: "Unread (New)", v: stats?.new ?? "—", i: <Mail size={14} /> },
            { k: "Responded", v: stats?.responded ?? "—", i: <CheckCircle2 size={14} /> },
          ].map((s) => (
            <div key={s.k} className="card-flat p-5">
              <div className="flex items-center gap-2" style={{ color: "var(--bronze)" }}>
                {s.i}
                <div className="overline" style={{ fontSize: 10 }}>{s.k}</div>
              </div>
              <div className="metric-value text-[34px] mt-2" style={{ color: "var(--ink)" }}>{s.v}</div>
            </div>
          ))}
        </div>

        {/* Website Analytics */}
        <div className="mb-10" data-testid="analytics-section">
          <div className="flex items-center gap-2 mb-3">
            <Globe size={14} style={{ color: "var(--bronze)" }} />
            <div className="overline">Website Analytics · Investor Brief Traffic</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { k: "Total Views", v: analytics?.total_views ?? "—" },
              { k: "Today", v: analytics?.today_views ?? "—" },
              { k: "This Week", v: analytics?.week_views ?? "—" },
              { k: "Unique Visitors", v: analytics?.unique_visitors ?? "—" },
              { k: "PDF Downloads", v: analytics?.pdf_downloads ?? "—" },
            ].map((s) => (
              <div key={s.k} className="card-flat p-5" data-testid={`analytics-${s.k.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="overline" style={{ fontSize: 10 }}>{s.k}</div>
                <div className="metric-value text-[32px] mt-2" style={{ color: "var(--ink)" }}>{s.v}</div>
              </div>
            ))}
          </div>
          <div className="card-flat overflow-hidden mt-4" data-testid="visitors-table">
            <div className="scroll-x">
              <table className="w-full text-left" style={{ minWidth: 640 }}>
                <thead>
                  <tr className="text-[11px] tracking-[0.14em] uppercase" style={{ color: "var(--ink-2)", fontFamily: "Outfit" }}>
                    <th className="py-3 px-4">When</th>
                    <th className="py-3 px-4">Visitor IP</th>
                    <th className="py-3 px-4">Device</th>
                    <th className="py-3 px-4">Came From</th>
                  </tr>
                </thead>
                <tbody>
                  {(analytics?.recent || []).length === 0 ? (
                    <tr>
                      <td colSpan={4} className="py-6 px-4 text-center text-[13px]" style={{ color: "var(--ink-2)" }}>
                        No visits recorded yet. Views appear here as investors open your brief.
                      </td>
                    </tr>
                  ) : (
                    analytics.recent.map((v) => (
                      <tr key={v.id} style={{ borderTop: "1px solid rgba(26,26,26,0.08)" }} data-testid={`visitor-row-${v.id}`}>
                        <td className="py-3 px-4 text-[13px] font-mono-data">{fmtIST(v.created_at)} IST</td>
                        <td className="py-3 px-4 text-[13px] font-mono-data">{v.ip}</td>
                        <td className="py-3 px-4 text-[13px]">{v.device}</td>
                        <td className="py-3 px-4 text-[13px]" style={{ color: "var(--ink-2)" }}>{v.referrer ? v.referrer : "Direct / typed URL"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 text-[11px]" style={{ color: "var(--ink-2)", borderTop: "1px solid rgba(26,26,26,0.06)", fontFamily: "Outfit" }}>
              You also receive an email digest (at most once every 30 min) whenever new investors view your brief. Your own admin visits are excluded.
            </div>
          </div>
        </div>

        {/* Enquiries */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5">
            <div className="flex items-center justify-between mb-3">
              <div className="overline">Investor Enquiries</div>
              <span className="text-[12px] font-mono-data" style={{ color: "var(--ink-2)" }}>
                {enquiries.length} total
              </span>
            </div>
            <div className="card-flat overflow-hidden" data-testid="enquiries-list">
              {loading ? (
                <div className="p-8 text-center text-[13px]" style={{ color: "var(--ink-2)" }}>Loading...</div>
              ) : enquiries.length === 0 ? (
                <div className="p-10 text-center">
                  <Inbox size={28} style={{ color: "var(--bronze)", margin: "0 auto" }} />
                  <div className="font-serif-display text-[20px] mt-3">No enquiries yet</div>
                  <div className="text-[12px] mt-1" style={{ color: "var(--ink-2)" }}>
                    Submissions from the contact form will appear here.
                  </div>
                </div>
              ) : (
                enquiries.map((q) => (
                  <button
                    key={q.id}
                    onClick={() => { setSelected(q); if (q.status === "new") updateStatus(q.id, "read"); }}
                    className="w-full text-left p-5 hover:bg-[rgba(212,175,55,0.05)] transition-colors"
                    style={{ borderTop: "1px solid rgba(26,26,26,0.08)", background: selected?.id === q.id ? "rgba(212,175,55,0.06)" : "transparent" }}
                    data-testid={`enquiry-row-${q.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-serif-display text-[18px]" style={{ color: "var(--ink)" }}>{q.name}</div>
                        <div className="text-[12px]" style={{ color: "var(--ink-2)" }}>{q.email} · {q.org || "—"}</div>
                      </div>
                      <StatusPill s={q.status} />
                    </div>
                    <div className="text-[13px] mt-2 line-clamp-2" style={{ color: "var(--ink-2)" }}>{q.message}</div>
                    <div className="text-[11px] mt-2 font-mono-data" style={{ color: "var(--ink-2)" }}>
                      {fmtIST(q.created_at)} IST
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="overline mb-3">Enquiry Detail</div>
            {selected ? (
              <div className="card-flat p-8" data-testid="enquiry-detail">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-serif-display text-[30px]" style={{ color: "var(--ink)" }}>{selected.name}</div>
                    <div className="text-[14px] mt-1" style={{ color: "var(--ink-2)" }}>
                      {selected.email} · {selected.org || "Independent"}
                    </div>
                    <div className="text-[12px] mt-1 font-mono-data" style={{ color: "var(--ink-2)" }}>
                      {fmtIST(selected.created_at)} IST
                    </div>
                  </div>
                  <StatusPill s={selected.status} />
                </div>

                <div className="mt-6 p-5" style={{ background: "var(--ivory-2)", border: "1px solid rgba(26,26,26,0.06)" }}>
                  <div className="overline mb-2">Message</div>
                  <p className="text-[14px] leading-relaxed whitespace-pre-wrap" style={{ color: "var(--ink)" }}>
                    {selected.message}
                  </p>
                </div>

                <div className="mt-6 flex flex-wrap gap-2">
                  <a href={`mailto:${selected.email}?subject=Re: Your enquiry to BEAUTWELLZ`} className="btn-primary" data-testid="reply-email-button">
                    <Mail size={14} /> Reply by Email
                  </a>
                  {STATUS_OPTIONS.filter(s => s !== selected.status).map((s) => (
                    <button
                      key={s}
                      onClick={() => updateStatus(selected.id, s)}
                      className="btn-ghost"
                      data-testid={`set-status-${s}`}
                    >
                      {s === "read" && <Eye size={14} />}
                      {s === "responded" && <CheckCircle2 size={14} />}
                      {s === "archived" && <Archive size={14} />}
                      Mark as {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="card-flat p-12 text-center">
                <Mail size={28} style={{ color: "var(--bronze)", margin: "0 auto" }} />
                <div className="font-serif-display text-[20px] mt-3">Select an enquiry</div>
                <div className="text-[12px] mt-1" style={{ color: "var(--ink-2)" }}>
                  Click any enquiry on the left to view full details and reply.
                </div>
              </div>
            )}

            {/* Quick guide */}
            <div className="card-flat p-6 mt-6" style={{ background: "var(--ivory-2)" }}>
              <div className="overline mb-2">External Channels</div>
              <div className="text-[13px]" style={{ color: "var(--ink-2)" }}>
                Investor brief is queued for listing on <strong style={{ color: "var(--ink)" }}>OpenVC.com</strong> and is
                eligible for <strong style={{ color: "var(--ink)" }}>UAE Make-it-in-the-Emirates / SME grant</strong> applications.
                Use the Download PDF button to share with bankers, family offices and UAE government partners.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
